// T220 · v2 ChatFAB — ground truth styles-v2.css §19 + main.js fabUpdate().
//
// SHELL AND VISIBILITY ONLY. Opening the panel is `onOpen()`, which the assembly
// wave points at the EXISTING assistant (`ChatAssistant.tsx`) — that file is not
// touched by this card and this component knows nothing about it.
//
// The show/hide rule exists because a permanently parked FAB covers body text on
// a phone, and covering a calendar slot button is worse than useless. So: hidden
// on the first screen (`scrollY <= 120`, zero first-screen overlap), hidden while
// the reader scrolls DOWN (they are reading, not asking), back on scroll-up or
// after 1.5s of stillness. The three thresholds are exported as named constants
// because `fabNextHidden` is a pure function and the four visibility states are
// unit-tested through it — a scroll-driven widget tested only by "it renders" is
// tested in exactly the state that never matters.
//
// ── T272 · that rule is a PHONE rule, and now says so in code ────────────────
// Read its own rationale again: "covers body text on a phone", "covers a
// calendar slot button". Both costs are paid at 390px. At 1440px the bottom
// right corner is empty, so hiding buys nothing there — while it charges the
// one price that matters on a wide screen: the site's only ask-a-question entry
// is invisible until the reader happens to scroll UP, which nobody does on
// purpose. Owner ruling A on FR-2026-08-01-a: desktop keeps the FAB, the phone
// keeps this rule. So the band is consulted in exactly two places — the scroll
// decision and the at-rest decision — through two thin wrappers, and
// `fabNextHidden` / `fabIdleHidden` / the three thresholds are byte-for-byte
// what T220 shipped. They are the phone's lock, not decoration.
//
// ── T274 · the band's edge moves 640 → 1024 ──────────────────────────────────
// Nothing about the structure above changes; one constant's VALUE does. See
// FAB_DESKTOP_QUERY's own comment for why 640 was the wrong edge (it declared
// "desktop" over the 640–1023 viewports that still open the mobile menu, and
// parked the bubble on top of the open drawer). The five phone artefacts are
// still byte-for-byte T220's.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cx } from '../cx';

/** below this scroll offset the FAB stays out of the first screen entirely */
export const FAB_TOP_THRESHOLD = 120;
/** stillness after which the FAB returns, in ms */
export const FAB_IDLE_MS = 1500;
/** the transition the card specifies for transform+opacity, in ms */
export const FAB_TRANSITION_MS = 150;

/**
 * The visibility rule, pure. Returns the next `hidden` value.
 *   y <= 120            → hidden (first screen)
 *   y > 120, scroll down → hidden
 *   y > 120, scroll up   → shown
 *   y > 120, no movement → unchanged (the idle timer decides — `fabIdleHidden`)
 */
export function fabNextHidden(y: number, lastY: number, current: boolean): boolean {
  if (y <= FAB_TOP_THRESHOLD) return true;
  if (y > lastY) return true;
  if (y < lastY) return false;
  return current;
}

/** After FAB_IDLE_MS of stillness: shown, unless we are back on the first screen. */
export function fabIdleHidden(y: number): boolean {
  return y <= FAB_TOP_THRESHOLD;
}

/**
 * T272 — the desktop band. T274 — where that band actually starts.
 *
 * T272 drew the line at 640px (Tailwind's stock `sm` edge) on the reasoning
 * that layouts stop being one column there. That was the wrong question. The
 * band exists to answer "is the reader using the mobile menu?", because the
 * cost the phone rule is paid to avoid is a bubble parked over content the
 * reader is trying to use — and the mobile menu is `lg:hidden` in
 * `src/components/feature/Header.tsx`, i.e. it is on screen all the way up to
 * 1023px. Between 640 and 1023 T272 therefore declared "desktop" over a
 * viewport that still opens a full-height drawer, and parked the FAB on top of
 * it: measured in production at 719×860, a `visible` FAB at [655, 796, 703,
 * 844] sitting over the open menu. T272's acceptance denominator listed
 * 1440/1280 and 390/320 and never looked at the band in between; the omission
 * is the hub's, on the record, and 1024 is the correction. The line is now the
 * SAME `lg` edge the nav itself uses, from one source of truth: where the
 * hamburger disappears is where desktop begins.
 *
 * Below 640px nothing whatsoever changes — the three thresholds and both pure
 * functions are byte-for-byte T220's, and 640–1023 simply rejoins the rule it
 * should never have left.
 *
 * Surveyed before inventing it (T272's instruction: reuse an existing
 * breakpoint hook if the repo has one): the repo has NO runtime breakpoint
 * hook — `matchMedia` appears exactly once in src/, in
 * `src/pages/intake/focus/juice.ts`, and it reads `prefers-reduced-motion`, not
 * a width. `tokens.ts`'s `v2Screens` are not a candidate either: they are
 * MAX-width band names (rem strings) fed to tailwind.config for the CSS
 * pipeline, plus `v2-min40` which exists specifically as the fee-table's
 * complement, not as a device-class line. So: one named constant, here, next to
 * the rule it gates.
 */
export const FAB_DESKTOP_QUERY = '(min-width: 1024px)';

/**
 * Visibility for one scroll event, desktop band folded in (T272).
 * Desktop: never hidden — the rule above is a phone rule.
 * Phone: `fabNextHidden`, untouched.
 */
export function fabHiddenOnScroll(desktop: boolean, y: number, lastY: number, current: boolean): boolean {
  if (desktop) return false;
  return fabNextHidden(y, lastY, current);
}

/**
 * Visibility whenever the page is NOT moving (T272): the mount settle, a band
 * change, and the FAB_IDLE_MS timer all ask the same question.
 * Desktop: never hidden. Phone: `fabIdleHidden`, untouched.
 */
export function fabHiddenAtRest(desktop: boolean, y: number): boolean {
  if (desktop) return false;
  return fabIdleHidden(y);
}

export interface V2ChatFABProps {
  /** opens the EXISTING assistant — this component never owns a panel */
  onOpen: () => void;
  /** accessible name; the visible glyph is decorative */
  label: string;
  icon?: ReactNode;
  className?: string;
}

export function ChatFAB({ onOpen, label, icon, className }: V2ChatFABProps) {
  // hidden is the safe first paint: the FAB must never appear over the first
  // screen, and SSR/prerender has no scroll position to consult.
  const [hidden, setHidden] = useState(true);
  const lastY = useRef(0);

  useEffect(() => {
    // The band is read once and then FOLLOWED: a window dragged across the edge, or
    // a phone turned landscape, must switch rules — a one-shot read would leave
    // a rotated phone permanently parked, which is the exact thing the phone
    // rule exists to prevent.
    const band = typeof window.matchMedia === 'function' ? window.matchMedia(FAB_DESKTOP_QUERY) : null;
    let desktop = band?.matches ?? false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    // Entering a band — at mount, or on a resize across the edge — settles from
    // the CURRENT scroll position: desktop shows immediately, phone drops
    // straight back under the first-screen rule it never left.
    const settle = () => {
      if (timer) clearTimeout(timer);
      lastY.current = window.scrollY;
      setHidden(fabHiddenAtRest(desktop, window.scrollY));
    };

    const onScroll = () => {
      const y = window.scrollY;
      setHidden((current) => fabHiddenOnScroll(desktop, y, lastY.current, current));
      lastY.current = y;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setHidden(fabHiddenAtRest(desktop, window.scrollY)), FAB_IDLE_MS);
    };

    const onBandChange = (event: MediaQueryListEvent) => {
      desktop = event.matches;
      settle();
    };

    settle();
    window.addEventListener('scroll', onScroll, { passive: true });
    band?.addEventListener('change', onBandChange);
    return () => {
      window.removeEventListener('scroll', onScroll);
      band?.removeEventListener('change', onBandChange);
      if (timer) clearTimeout(timer);
    };
  }, []);

  return (
    <div
      className={cx(
        'v2-fab fixed right-5 bottom-5 z-[150] v2-max48:right-4 v2-max48:bottom-4',
        'transition-[opacity,transform,visibility] duration-150 ease-[ease]',
        hidden ? 'opacity-0 translate-y-3 invisible pointer-events-none' : 'opacity-100 translate-y-0 visible',
        className,
      )}
      data-fab-hidden={hidden ? 'true' : 'false'}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-label={label}
        // 48px on every viewport — I32's floating-element floor, not just the
        // 44px touch floor.
        className={
          'v2-fab-btn flex h-12 w-12 items-center justify-center rounded-full bg-teal-700 text-white ' +
          'shadow-v2-lift transition-[background-color,transform] duration-150 ease-[ease] ' +
          'hover:bg-teal-600 hover:-translate-y-0.5'
        }
      >
        <span aria-hidden="true" className="flex h-[22px] w-[22px] items-center justify-center">
          {icon}
        </span>
      </button>
    </div>
  );
}
